import {
  Alert,
  Box,
  Button,
  Dialog,
  HStack,
  Input,
  Portal,
  Text,
  VStack,
} from "@chakra-ui/react";
import { AlertCircle, CheckCircle2, Wallet } from "lucide-react";
import { useState } from "react";
import { useCreateWithdrawal } from "../hooks/useCreateWithdrawal";
import { toaster } from "../../../../components/ui/toaster";

interface WithdrawalModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function WithdrawalModal({
  isOpen,
  onClose,
}: WithdrawalModalProps) {
  const [amount, setAmount] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { createWithdrawal, isCreating } = useCreateWithdrawal(
    () => {
      toaster.create({
        title: "Retiro exitoso",
        description: "El retiro se ha procesado correctamente.",
        type: "success",
        duration: 5000,
        meta: { closable: true },
      });
      handleCloseAndReset();
    },
    () => {
      toaster.create({
        title: "Error",
        description: "Error al procesar el retiro. Por favor intenta de nuevo.",
        type: "error",
        duration: 5000,
        meta: { closable: true },
      });
    }
  );

  const formatCurrencyInput = (value: string) => {
    const numericValue = value.replace(/[^0-9]/g, "");
    if (!numericValue) return "";
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      maximumFractionDigits: 0,
    }).format(parseInt(numericValue, 10));
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      maximumFractionDigits: 0,
    }).format(value);
  };

  const handleContinue = () => {
    setShowConfirmation(true);
  };

  const handleSubmit = async () => {
    const parsedAmount = parseFloat(amount);
    if (!parsedAmount || parsedAmount <= 0) return;

    createWithdrawal(parsedAmount);
  };

  const handleCloseAndReset = () => {
    setAmount("");
    setShowConfirmation(false);
    onClose();
  };

  const handleClose = () => {
    setAmount("");
    setShowConfirmation(false);
    onClose();
  };

  const parsedAmount = parseFloat(amount);
  const isValidAmount = parsedAmount >= 1000;
  const minAmountError = amount && parsedAmount > 0 && parsedAmount < 1000;

  return (
    <Dialog.Root
      lazyMount
      open={isOpen}
      onOpenChange={(e) => !e.open && handleClose()}
      size="md"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header borderBottom="1px solid #ebecef80" p={4}>
              <Dialog.Title fontSize="lg" fontWeight="semibold">
                Nuevo retiro a cuenta propia
              </Dialog.Title>
            </Dialog.Header>
            <Dialog.CloseTrigger position="top-end" top={3} />
            <Dialog.Body py={6}>
              {!showConfirmation ? (
                <VStack gap={4} align="stretch">
                  <Text color="gray.600" fontSize="sm">
                    Ingresa el monto que deseas retirar a tu cuenta registrada.
                  </Text>
                  <Box>
                    <Text fontWeight="medium" mb={2}>
                      Monto a retirar
                    </Text>
                    <Input
                      type="text"
                      value={amount}
                      onChange={(e) => {
                        const numeric = e.target.value.replace(/[^0-9]/g, "");
                        setAmount(numeric);
                      }}
                      placeholder="0"
                      size="lg"
                      min={0}
                    />
                    {amount && (
                      <Text
                        fontSize="sm"
                        color="gray.500"
                        mt={1}
                        fontWeight="medium"
                      >
                        {formatCurrencyInput(amount)}
                      </Text>
                    )}
                    {minAmountError && (
                      <Text fontSize="sm" color="red.500" mt={1}>
                        El monto mínimo es de $1.000 COP
                      </Text>
                    )}
                  </Box>
                  <Alert.Root status="info" colorPalette="blue">
                    <Alert.Indicator>
                      <AlertCircle size={16} />
                    </Alert.Indicator>
                    <Alert.Content>
                      <Alert.Title fontSize="sm" fontWeight="medium">
                        Información importante
                      </Alert.Title>
                      <Alert.Description fontSize="xs">
                        Recuerda que si ya has creado un retiro a cuenta propia
                        el día de hoy, este retiro tendrá costo. Solo un retiro
                        a cuenta propia al día es exento de cobro.
                      </Alert.Description>
                    </Alert.Content>
                  </Alert.Root>
                  <Text fontSize="xs" color="gray.500">
                    El monto se transferirá a tu cuenta registrada en máximo
                    24-48 horas hábiles.
                  </Text>
                </VStack>
              ) : (
                <VStack gap={4} align="stretch">
                  <Alert.Root status="warning" colorPalette="orange">
                    <Alert.Indicator>
                      <AlertCircle size={16} />
                    </Alert.Indicator>
                    <Alert.Content>
                      <Alert.Title fontSize="sm" fontWeight="medium">
                        Confirma tu retiro
                      </Alert.Title>
                    </Alert.Content>
                  </Alert.Root>

                  <Box
                    p={4}
                    bg="gray.50"
                    borderRadius="lg"
                    border="1px solid"
                    borderColor="gray.200"
                  >
                    <HStack justify="space-between" align="center">
                      <HStack gap={3}>
                        <Box p={2} bg="palomma.100" borderRadius="md">
                          <Wallet size={20} color="#8e5cff" />
                        </Box>
                        <VStack align="start" gap={0}>
                          <Text fontSize="sm" color="gray.500">
                            Monto a retirar
                          </Text>
                          <Text
                            fontSize="xl"
                            fontWeight="bold"
                            color="gray.800"
                          >
                            {formatCurrency(parsedAmount)}
                          </Text>
                        </VStack>
                      </HStack>
                      <CheckCircle2 size={24} color="#22c55e" />
                    </HStack>
                  </Box>

                  <Text fontSize="xs" color="gray.500">
                    Al confirmar, recibirás el monto en tu cuenta registrada en
                    24-48 horas hábiles.
                  </Text>
                </VStack>
              )}
            </Dialog.Body>
            <Dialog.Footer borderTop="1px solid #ebecef80" p={4}>
              <Button
                variant="ghost"
                onClick={
                  showConfirmation
                    ? () => setShowConfirmation(false)
                    : handleClose
                }
                disabled={isCreating}
              >
                {showConfirmation ? "Volver" : "Cancelar"}
              </Button>
              <Button
                colorPalette="palomma"
                onClick={showConfirmation ? handleSubmit : handleContinue}
                loading={isCreating}
                disabled={!isValidAmount}
              >
                {showConfirmation ? "Confirmar Retiro" : "Continuar"}
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
